import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import type { PoliticaComissaoRequest, PoliticaComissaoResponse } from '@/types/simulacaoComissao'
import { fracaoParaPercentual, lerPercentualPtBr } from '../fracao'
import { inicioPadraoProposta } from './inicioPadrao'
import { errosDaFalhaComissao, HELPER_ORIGEM } from './errosFormulario'

interface Props {
  aberto: boolean
  /**
   * Versão vigente (do backend) usada só para PRÉ-PREENCHER o editor — nenhuma faixa ou
   * percentual nasce de constante da tela. Sem vigente, o editor começa com uma faixa vazia.
   */
  base: PoliticaComissaoResponse | null
  onFechar: () => void
  onSalvar: (request: PoliticaComissaoRequest) => Promise<void>
}

interface LinhaFaixa {
  /** Vazio na última (sem teto). */
  valorAte: string
  ateInclusivo: boolean
  /** Percentual digitado ("20" = 20%). */
  percentual: string
}

const LINHA_VAZIA: LinhaFaixa = { valorAte: '', ateInclusivo: false, percentual: '' }

/** BRL → texto pt-BR do campo ("1.234.567,89"). Só formatação. */
const valorParaTexto = (v: number | null): string =>
  v == null ? '' : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Fração → percentual digitável ("0,2" → "20"). Só mudança de unidade. */
const fracaoParaTexto = (f: number): string =>
  fracaoParaPercentual(f).toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false })

/**
 * Escala das colunas do backend (V11): `valor_ate NUMERIC(18,2)` → até 16 dígitos inteiros e
 * 2 casas; percentual `NUMERIC(9,6)` em FRAÇÃO → até 4 casas no percentual digitado. Fora disso
 * o backend recusa (422 `FAIXAS_INVALIDAS`); a tela recusa antes, sem arredondar em silêncio.
 */
export const CASAS_VALOR = 2
export const CASAS_PERCENTUAL = 4

/**
 * Lê um valor em R$ digitado em pt-BR: "1.234.567,89", "1234567", "1.234.567", "1234567,8".
 * `null` quando não é número positivo ou passa da escala (mais de 2 casas ou 16 dígitos
 * inteiros). Só leitura de formato — sem regra de negócio.
 */
export const lerValorPtBr = (texto: string): number | null => {
  const limpo = texto.replace(/R\$/gi, '').replace(/\s/g, '')
  if (limpo === '') return null
  let normalizado: string
  if (limpo.includes(',')) normalizado = limpo.replace(/\./g, '').replace(',', '.')
  else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) normalizado = limpo.replace(/\./g, '')
  else normalizado = limpo
  if (!new RegExp(`^\\d{1,16}(\\.\\d{1,${CASAS_VALOR}})?$`).test(normalizado)) return null
  const n = Number(normalizado)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Casas decimais do percentual como digitado (mesma normalização de `lerPercentualPtBr`). */
const casasDoPercentual = (texto: string): number => {
  const limpo = texto.replace(/%/g, '').replace(/\s/g, '')
  const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo
  const ponto = normalizado.indexOf('.')
  return ponto < 0 ? 0 : normalizado.length - ponto - 1
}

/** Percentual digitado ("12,3456") → número, ou `null` se inválido ou com mais de 4 casas. */
export const lerPercentualComissao = (texto: string): number | null => {
  const pct = lerPercentualPtBr(texto)
  return pct == null || casasDoPercentual(texto) > CASAS_PERCENTUAL ? null : pct
}

/** Percentual com até 4 casas → fração com até 6 casas, sem ruído binário (12,3456 → 0.123456). */
export const percentualParaFracaoComissao = (percentual: number): number =>
  Math.round(percentual * 10 ** CASAS_PERCENTUAL) / 10 ** (CASAS_PERCENTUAL + 2)

const linhasDe = (base: PoliticaComissaoResponse | null): LinhaFaixa[] => {
  const faixas = [...(base?.faixas ?? [])].sort((a, b) => a.ordem - b.ordem)
  if (faixas.length === 0) return [{ ...LINHA_VAZIA }]
  return faixas.map((f) => ({
    valorAte: valorParaTexto(f.valorAte),
    ateInclusivo: f.ateInclusivo,
    percentual: fracaoParaTexto(f.percentual),
  }))
}

/**
 * Nova versão da política de comissão GLOBAL (AN-PP4 §2.5): faixas por valor da operação
 * ("até (R$)", "inclusive", "percentual"; a última sem teto) e o percentual da receita
 * líquida do Sacado/Sacador 360. Percentual na tela, FRAÇÃO na API. A coerência das faixas
 * (crescentes, sem lacuna/sobreposição) é validada pelo backend (422 `FAIXAS_INVALIDAS`);
 * aqui só formato e presença. `versao` e `versaoFaixas` são automáticas no backend.
 */
export const NovaPoliticaComissaoDialog: FC<Props> = ({ aberto, base, onFechar, onSalvar }) => {
  const [linhas, setLinhas] = useState<LinhaFaixa[]>([{ ...LINHA_VAZIA }])
  const [percentual360, setPercentual360] = useState('')
  const [vigenciaInicio, setVigenciaInicio] = useState(() => inicioPadraoProposta())
  const [origem, setOrigem] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (aberto) {
      setLinhas(linhasDe(base))
      setPercentual360(base ? fracaoParaTexto(base.percentualReceitaLiquida360) : '')
      setVigenciaInicio(inicioPadraoProposta())
      setOrigem('')
      setErros({})
      setErroGeral(null)
    }
  }, [aberto, base])

  const ultima = linhas.length - 1

  const alterar = (i: number, parcial: Partial<LinhaFaixa>) =>
    setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, ...parcial } : l)))

  /** Nova faixa entra ANTES da última (a última continua sem teto). */
  const adicionar = () =>
    setLinhas((ls) => [...ls.slice(0, ls.length - 1), { ...LINHA_VAZIA }, ls[ls.length - 1]])

  const remover = (i: number) => setLinhas((ls) => ls.filter((_, j) => j !== i))

  const salvar = async () => {
    const faltando: Record<string, string> = {}
    const faixas = linhas.map((l, i) => {
      const semTeto = i === ultima
      const valorAte = semTeto ? null : lerValorPtBr(l.valorAte)
      if (!semTeto && valorAte == null)
        faltando[`faixas[${i}].valorAte`] =
          'Informe o limite em R$ (ponto no milhar, vírgula nos centavos; até 2 casas)'
      const pct = lerPercentualComissao(l.percentual)
      if (pct == null) faltando[`faixas[${i}].percentual`] = 'Percentual com vírgula, até 4 casas decimais'
      return {
        ordem: i + 1,
        valorAte,
        ateInclusivo: semTeto ? false : l.ateInclusivo,
        percentual: pct == null ? 0 : percentualParaFracaoComissao(pct),
      }
    })
    const pct360 = lerPercentualComissao(percentual360)
    if (pct360 == null)
      faltando.percentualReceitaLiquida360 = 'Informe o percentual em %, com vírgula, até 4 casas decimais'
    if (!vigenciaInicio) faltando.vigenciaInicio = 'Informe o início da vigência'
    if (!origem.trim()) faltando.origem = 'Informe a origem (e-mail, ata ou aprovação do Financeiro)'
    if (Object.keys(faltando).length) {
      setErros(faltando)
      return
    }
    setSalvando(true)
    setErros({})
    setErroGeral(null)
    try {
      await onSalvar({
        faixas,
        percentualReceitaLiquida360: percentualParaFracaoComissao(pct360 as number),
        vigenciaInicio,
        origem: origem.trim(),
      })
      onFechar()
    } catch (e) {
      const r = errosDaFalhaComissao(e)
      setErros(r.porCampo)
      setErroGeral(r.geral)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Propor versão — política de comissão</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erroGeral && <Alert severity="error">{erroGeral}</Alert>}
          <Alert severity="info">
            É uma PROPOSTA: só passa a valer depois da aprovação por outra pessoa (FINANCEIRO). Aprovada, vale
            para todos os produtos de faixa e para a Campanha de Incentivo a partir do início da vigência, e a
            versão aberta é encerrada nessa data. Simulações já gravadas não mudam.
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Faixas por valor da operação
          </Typography>
          {erros.faixas && (
            <Alert severity="error" role="alert" data-testid="erro-faixas">
              {erros.faixas}
            </Alert>
          )}
          <Stack spacing={1.5} role="list" aria-label="Faixas da nova versão">
            {linhas.map((l, i) => {
              const semTeto = i === ultima
              return (
                <Stack
                  key={i}
                  role="listitem"
                  aria-label={`Faixa ${i + 1}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'flex-start' }}
                >
                  <Typography variant="body2" sx={{ minWidth: 64, pt: { sm: 1 } }}>
                    Faixa {i + 1}
                  </Typography>
                  <TextField
                    size="small"
                    label="Até (R$)"
                    value={semTeto ? '' : l.valorAte}
                    placeholder={semTeto ? 'Sem teto' : undefined}
                    onChange={(e) => alterar(i, { valorAte: e.target.value })}
                    disabled={semTeto}
                    error={!!erros[`faixas[${i}].valorAte`]}
                    helperText={erros[`faixas[${i}].valorAte`] ?? (semTeto ? 'Última faixa: sem teto' : undefined)}
                    inputProps={{ inputMode: 'decimal', 'aria-label': `Até (R$) da faixa ${i + 1}` }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!semTeto && l.ateInclusivo}
                        disabled={semTeto}
                        onChange={(e) => alterar(i, { ateInclusivo: e.target.checked })}
                        inputProps={{ 'aria-label': `Inclusive na faixa ${i + 1}` }}
                      />
                    }
                    label="inclusive"
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    size="small"
                    label="Percentual"
                    value={l.percentual}
                    onChange={(e) => alterar(i, { percentual: e.target.value })}
                    error={!!erros[`faixas[${i}].percentual`]}
                    helperText={erros[`faixas[${i}].percentual`]}
                    inputProps={{ inputMode: 'decimal', 'aria-label': `Percentual da faixa ${i + 1}` }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    aria-label={`Remover faixa ${i + 1}`}
                    onClick={() => remover(i)}
                    disabled={linhas.length === 1 || semTeto}
                    size="small"
                    sx={{ mt: { sm: 0.5 } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )
            })}
          </Stack>
          <Button size="small" startIcon={<AddIcon />} onClick={adicionar} sx={{ alignSelf: 'flex-start' }}>
            Adicionar faixa
          </Button>

          <TextField
            label="Percentual da receita líquida Originei (Sacado/Sacador 360)"
            value={percentual360}
            onChange={(e) => setPercentual360(e.target.value)}
            error={!!erros.percentualReceitaLiquida360}
            helperText={erros.percentualReceitaLiquida360 ?? 'Sem faixa de volume — a API guarda como fração'}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            required
            fullWidth
          />
          <TextField
            label="Início da vigência"
            type="date"
            value={vigenciaInicio}
            onChange={(e) => setVigenciaInicio(e.target.value)}
            error={!!erros.vigenciaInicio}
            helperText={erros.vigenciaInicio ?? 'Padrão: amanhã — a aprovação precisa acontecer até o início'}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          <TextField
            label="Origem"
            value={origem}
            onChange={(e) => setOrigem(e.target.value)}
            error={!!erros.origem}
            helperText={erros.origem ?? HELPER_ORIGEM}
            inputProps={{ maxLength: 200 }}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={salvando}
          startIcon={salvando ? <CircularProgress size={18} /> : undefined}
        >
          Enviar para aprovação
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NovaPoliticaComissaoDialog
